// mds-dojo — dijit/typematic — FAITHFUL port of references/dijit/typematic.js.
// Repeatedly fires a callback while a key/mouse is held (used by _PaletteMixin arrow-key
// navigation). Only one typematic sequence runs page-wide at a time.
//
// ADAPTATIONS for this package:
//  - `dojo/_base/declare` n/a; `dojo/sniff` → `../dojo/has`; other dojo ids → relative.
//  - `./main` (dijit) kept so the legacy global `dijit.typematic` still resolves.
//  - `connect._keypress` referenced via `(connect as any)` (only used for the deprecated
//    charOrCode listener path, which _PaletteMixin never triggers — it always passes keyCode).
import array from '../dojo/_base/array'; // array.forEach
import connect from '../dojo/_base/connect'; // connect._keypress
import lang from '../dojo/_base/lang'; // lang.mixin, lang.hitch
import on from '../dojo/on';
import has from '../dojo/has'; // has("ie")
import dijit from './main'; // setting dijit.typematic global

// module:
//		dijit/typematic

var typematic: any = ((dijit as any).typematic = {
	// summary:
	//		These functions are used to repetitively call a user specified callback
	//		method when a specific key or mouse click over a specific DOM node is
	//		held down for a specific amount of time.
	//		Only 1 such event is allowed to occur on the browser page at 1 time.

	_fireEventAndReload: function (this: any) {
		this._timer = null;
		this._callback(++this._count, this._node, this._evt);

		// Schedule next event, timer is at most minDelay (default 10ms) to avoid
		// browser overload (particularly avoiding starving DOH robot so it never gets to send a mouseup)
		this._currentTimeout = Math.max(
			this._currentTimeout < 0 ? this._initialDelay :
				(this._subsequentDelay > 1 ? this._subsequentDelay : Math.round(this._currentTimeout * this._subsequentDelay)),
			this._minDelay);
		this._timer = setTimeout(lang.hitch(this, "_fireEventAndReload"), this._currentTimeout);
	},

	trigger: function (/*Event*/ evt: any, /*Object*/ _this: any, /*DOMNode*/ node: any, /*Function*/ callback: any, /*Object*/ obj: any, /*Number?*/ subsequentDelay?: any, /*Number?*/ initialDelay?: any, /*Number?*/ minDelay?: any) {
		// summary:
		//		Start a timed, repeating callback sequence.
		//		If already started, the function call is ignored.
		//		This method is not normally called by the user but can be
		//		when the normal listener code is insufficient.
		if (obj != this._obj) {
			this.stop();
			this._initialDelay = initialDelay || 500;
			this._subsequentDelay = subsequentDelay || 0.90;
			this._minDelay = minDelay || 10;
			this._obj = obj;
			this._node = node;
			this._currentTimeout = -1;
			this._count = -1;
			this._callback = lang.hitch(_this, callback);
			this._evt = { faux: true };
			for (var attr in evt) {
				if (attr != "layerX" && attr != "layerY") { // prevent WebKit warnings
					var v = evt[attr];
					if (typeof v != "function" && typeof v != "undefined") {
						this._evt[attr] = v;
					}
				}
			}
			this._fireEventAndReload();
		}
	},

	stop: function (this: any) {
		// summary:
		//		Stop an ongoing timed, repeating callback sequence.
		if (this._timer) {
			clearTimeout(this._timer);
			this._timer = null;
		}
		if (this._obj) {
			this._callback(-1, this._node, this._evt);
			this._obj = null;
		}
	},

	addKeyListener: function (/*DOMNode*/ node: any, /*Object*/ keyObject: any, /*Object*/ _this: any, /*Function*/ callback: any, /*Number*/ subsequentDelay: any, /*Number*/ initialDelay: any, /*Number?*/ minDelay?: any) {
		// summary:
		//		Start listening for a specific typematic key.
		//		See also the trigger method for other parameters.
		// returns:
		//		a connection handle

		// Setup keydown or keypress listener depending on whether keyCode or charCode was specified.
		// If charOrCode is specified use deprecated connect._keypress synthetic event (remove for 2.0)
		var type = "keyCode" in keyObject ? "keydown" : "charCode" in keyObject ? "keypress" : (connect as any)._keypress,
			attr = "keyCode" in keyObject ? "keyCode" : "charCode" in keyObject ? "charCode" : "charOrCode";

		var handles = [
			on(node, type, lang.hitch(this, function (this: any, evt: any) {
				if (evt[attr] == keyObject[attr] &&
					(keyObject.ctrlKey === undefined || keyObject.ctrlKey == evt.ctrlKey) &&
					(keyObject.altKey === undefined || keyObject.altKey == evt.altKey) &&
					(keyObject.metaKey === undefined || keyObject.metaKey == (evt.metaKey || false)) && // IE doesn't even set metaKey
					(keyObject.shiftKey === undefined || keyObject.shiftKey == evt.shiftKey)) {
					evt.stopPropagation();
					evt.preventDefault();
					typematic.trigger(evt, _this, node, callback, keyObject, subsequentDelay, initialDelay, minDelay);
				} else if (typematic._obj == keyObject) {
					typematic.stop();
				}
			})),
			on(node, "keyup", lang.hitch(this, function () {
				if (typematic._obj == keyObject) {
					typematic.stop();
				}
			}))
		];
		return {
			remove: function () {
				array.forEach(handles, function (h: any) {
					h.remove();
				});
			}
		};
	},

	addMouseListener: function (/*DOMNode*/ node: any, /*Object*/ _this: any, /*Function*/ callback: any, /*Number*/ subsequentDelay: any, /*Number*/ initialDelay: any, /*Number?*/ minDelay?: any) {
		// summary:
		//		Start listening for a typematic mouse click.
		//		See the trigger method for other parameters.
		// returns:
		//		a connection handle
		var handles = [
			on(node, "mousedown", lang.hitch(this, function (this: any, evt: any) {
				evt.preventDefault();
				typematic.trigger(evt, _this, node, callback, node, subsequentDelay, initialDelay, minDelay);
			})),
			on(node, "mouseup", lang.hitch(this, function (this: any, evt: any) {
				if (this._obj) {
					evt.preventDefault();
				}
				typematic.stop();
			})),
			on(node, "mouseout", lang.hitch(this, function (this: any, evt: any) {
				if (this._obj) {
					evt.preventDefault();
				}
				typematic.stop();
			})),
			on(node, "dblclick", lang.hitch(this, function (this: any, evt: any) {
				evt.preventDefault();
				if (has("ie") < 9) {
					typematic.trigger(evt, _this, node, callback, node, subsequentDelay, initialDelay, minDelay);
					setTimeout(lang.hitch(this, typematic.stop), 50);
				}
			}))
		];
		return {
			remove: function () {
				array.forEach(handles, function (h: any) {
					h.remove();
				});
			}
		};
	},

	addListener: function (/*Node*/ mouseNode: any, /*Node*/ keyNode: any, /*Object*/ keyObject: any, /*Object*/ _this: any, /*Function*/ callback: any, /*Number*/ subsequentDelay: any, /*Number*/ initialDelay: any, /*Number?*/ minDelay?: any) {
		// summary:
		//		Start listening for a specific typematic key and mouseclick.
		//		This is a thin wrapper to addKeyListener and addMouseListener.
		//		See the addMouseListener and addKeyListener methods for other parameters.
		// returns:
		//		a connection handle
		var handles = [
			this.addKeyListener(keyNode, keyObject, _this, callback, subsequentDelay, initialDelay, minDelay),
			this.addMouseListener(mouseNode, _this, callback, subsequentDelay, initialDelay, minDelay)
		];
		return {
			remove: function () {
				array.forEach(handles, function (h: any) {
					h.remove();
				});
			}
		};
	}
});

export default typematic;
